
import { checkPrinterStatus, printText, setCharacterset, setPosId } from '@/libs/bixolon/bxlpos'
// import state from '@/state'

export const printerInit = (issueID) => {
  checkPrinterStatus()
  setCharacterset(1250)
  setPosId(issueID)
}

export const calculateLineLength = (horizontal, vertical, lineLength) => {
  // Width is determined by horizontal scale (vertical affects height, not width)
  // ESC/POS scale: h=0 is 1x (normal), h=1 is 2x width, h=2 is 3x width, h=3 is 4x width
  // So available characters = lineLength / (horizontal + 1)
  const scaleFactor = horizontal + 1
  return Math.round(lineLength / scaleFactor)
}

export const align = (text, options) => {
  const lineLength = calculateLineLength(options.horizontal, options.vertical, options.printerConfiguration.line_length)
  const textLength = text.length
  let startPosition = 0
  switch (options.alignment) {
    case 0:
      break
    case 1:
      startPosition = Math.round(lineLength / 2) - Math.round(textLength / 2)
      break
    case 2:
      startPosition = lineLength - textLength
      break
  }
  if (startPosition < 0) startPosition = 0
  return `${' '.repeat(startPosition)}${text}`
}

/**
 * Word-wrap text with prefix/indent support, respecting font scaling
 * @param {string} text - Text to wrap
 * @param {string} firstLinePrefix - Prefix for first line (e.g., "1 X ")
 * @param {string} continuationIndent - Indent for continuation lines (usually spaces matching prefix length)
 * @param {number} horizontal - Horizontal font scale (0-4)
 * @param {number} baseLineLength - Base line length at normal font size
 * @returns {string[]} Array of lines (without prefix/indent - caller adds them)
 */
export const wrapTextWithPrefix = (text, firstLinePrefix, continuationIndent, horizontal, baseLineLength) => {
  // Calculate actual characters that fit on line considering font scaling
  const maxCharsPerLine = calculateLineLength(horizontal, 0, baseLineLength)

  const lines = []
  const words = text.split(' ')
  let currentLine = ''

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const isFirstLine = lines.length === 0
    const prefix = isFirstLine ? firstLinePrefix : continuationIndent

    // Test if adding this word to current line would fit
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const fullLine = prefix + testLine

    if (fullLine.length <= maxCharsPerLine) {
      // Fits, add to current line
      currentLine = testLine
    } else {
      // Doesn't fit - move whole word to next line
      if (currentLine) {
        // Save current line and move this word to next line
        lines.push(currentLine)
        currentLine = word
      } else {
        // Current line is empty but word itself doesn't fit - force it
        lines.push(word)
        currentLine = ''
      }
    }
  }

  // Add remaining text
  if (currentLine) {
    lines.push(currentLine)
  }

  return lines.length > 0 ? lines : [text]
}

/**
 * Prints text with configurable line spacing
 * @param {string} text - Text to print
 * @param {Object} options - Print options including lineSpacing (0=smaller, 1=normal, 2=larger)
 */
export const printLineWithSpacing = (text, options) => {
  printLine(text, options)

  // Add extra line feed for larger spacing
  if (options.lineSpacing === 2) {
    if (options.printerConfiguration.library === 'UNIVERSAL') {
      options.document.feed(1)
    } else {
      // For BIXOLON, add empty line
      printLine('', { ...options, lineSpacing: 1 })
    }
  }
}

export const printLine = (text, options) => {
  if (options.printerConfiguration.library === 'UNIVERSAL') {
    const bold = !!options.bold
    if (bold) {
      options.document.style([options.escpos.FontStyle.Bold])
    } else {
      options.document.style([options.escpos.FontStyle.Normal])
    }

    options.document.setCharacterCodeTable(options.printerConfiguration.char_table ?? 19)

    // Set font type (A, B, or C)
    if (options.font) {
      options.document.font(options.escpos.FontFamily[options.font])
    }

    // Set size
    options.document.size(options.horizontal, options.vertical)

    // Use manual align() helper function to add spaces for centering/right alignment
    const alignedText = align(text, options)

    // Print text with manual alignment (spaces added by align() function)
    // lineSpacing: 0=no newline (smaller spacing), 1+=with newline
    const newline = (options.lineSpacing === 0) ? '' : '\n'
    options.document.text(alignedText + newline, options.printerConfiguration.code_page ?? 1250)
  } else {
    printText(
      text + '\n',
      options.horizontal,
      options.vertical,
      options.bold,
      options.invert,
      options.underline,
      options.font,
      options.alignment)
  }
  // console.info(text)
}

export const printItems = (parts, middleColOffset, middleColOffset2, options) => {
  const lineLength = options.printerConfiguration.line_length
  const segmentLength = Math.ceil(lineLength / parts.length)
  let text = ''
  const ostatakZnakova = lineLength - (parts[1].length + parts[2].length + parts[3].length + 15)

  let veci = false
  if (parts[0].length > ostatakZnakova) {
    veci = true
    if (options.printerConfiguration.library === 'UNIVERSAL') {
      printLine(parts[0], options)
    } else {
      printText(
        parts[0] + '\n',
        options.horizontal,
        options.vertical,
        options.bold,
        options.invert,
        options.underline,
        options.font,
        options.alignment)
    }
    // text += ' '.repeat(segmentLength + 5)
  } else {
    text += parts[0]
  }
  let repeat = 0
  if (veci === false) { repeat = ((segmentLength * 2 + 5) - parts[1].length - parts[0].length) < 0 ? (segmentLength * 2 + 5 - parts[1].length) : ((segmentLength * 2 + 5) - parts[1].length - parts[0].length) } else { repeat = (segmentLength * 2 + 5) - parts[1].length }
  text += ' '.repeat(repeat) + parts[1]
  if (parts.length > 3) {
    text += ' '.repeat(segmentLength - parts[2].length - middleColOffset2) + parts[2]
  } else {
    text += ' '.repeat(lineLength - text.length - parts[2].length) + parts[2]
  }
  if (parts.length > 3) {
    text += ' '.repeat((lineLength - text.length - parts[3].length) < 0 ? 0 : lineLength - text.length - parts[3].length) + parts[3]
  }

  if (options.printerConfiguration.library === 'UNIVERSAL') {
    printLine(text, options)
  } else {
    printText(
      text + '\n',
      options.horizontal,
      options.vertical,
      options.bold,
      options.invert,
      options.underline,
      options.font,
      options.alignment)
  }
  // console.info(text)
}

export const printMultiple = (parts, middleColOffset, middleColOffset2, options) => {
  const lineLength = options.printerConfiguration.line_length
  const segmentLength = Math.ceil(lineLength / parts.length)

  let text = ''
  let repeat = 0
  if ((segmentLength - parts[0].length) < 0) {
    if (options.printerConfiguration.library === 'UNIVERSAL') {
      printLine(parts[0], options)
    } else {
      printText(
        parts[0] + '\n',
        options.horizontal,
        options.vertical,
        options.bold,
        options.invert,
        options.underline,
        options.font,
        options.alignment)
    }
    text += ' '.repeat(segmentLength + 5)
  } else {
    repeat = segmentLength - parts[0].length + 5 < 0 ? 0 : segmentLength - parts[0].length + 5
    text += parts[0] + ' '.repeat(repeat)
  }
  repeat = segmentLength - parts[1].length - middleColOffset < 0 ? 0 : segmentLength - parts[1].length - middleColOffset
  text += ' '.repeat(repeat) + parts[1]
  if (parts.length > 3) {
    repeat = segmentLength - parts[2].length - middleColOffset2 < 0 ? 0 : segmentLength - parts[2].length - middleColOffset2
    text += ' '.repeat(repeat) + parts[2]
  } else {
    repeat = lineLength - text.length - parts[2].length < 0 ? 0 : lineLength - text.length - parts[2].length
    text += ' '.repeat(repeat) + parts[2]
  }
  if (parts.length > 3) {
    text += ' '.repeat((lineLength - text.length - parts[3].length) < 0 ? 0 : lineLength - text.length - parts[3].length) + parts[3]
  }

  if (options.printerConfiguration.library === 'UNIVERSAL') {
    printLine(text, options)
  } else {
    printText(
      text + '\n',
      options.horizontal,
      options.vertical,
      options.bold,
      options.invert,
      options.underline,
      options.font,
      options.alignment)
  }
  // console.info(text)
}

export const printMultipleShort = (parts, offset, options) => {
  // Offset is the number of characters by which the middle column will move to the left
  const lineLength = options.printerConfiguration.line_length
  const segmentLength = lineLength / parts.length

  const part1 = String(parts[0])
  let text = parts[0] + ' '.repeat(segmentLength - part1.length)
  if (parts[1]) {
    const part2 = String(parts[1])
    const spaceAround = segmentLength - part2.length
    text += ' '.repeat(spaceAround - offset) + part2
    if (parts[2]) {
      const part3 = String(parts[2])
      const spaceBefore = segmentLength - part3.length
      text += ' '.repeat(spaceBefore + offset) + part3
      if (parts[3]) {
        const part4 = String(parts[3])
        const currentLength = text.length
        const remainingPart = lineLength - currentLength - part4.length
        text += ' '.repeat(remainingPart) + part4
      }
    }
  }

  if (options.printerConfiguration.library === 'UNIVERSAL') {
    printLine(text, options)
  } else {
    printText(
      text + '\n',
      options.horizontal,
      options.vertical,
      options.bold,
      options.invert,
      options.underline,
      options.font,
      options.alignment)
  }
  // console.info(text)
}

export const printTotals = (label, amount, options) => {
  const lineLength = options.printerConfiguration.line_length
  let separation = lineLength - label.length - amount.length
  if (separation < 0) { separation = 0 }
  const text = label + ' '.repeat(separation) + amount

  if (options.printerConfiguration.library === 'UNIVERSAL') {
    printLine(text, options)
  } else {
    printText(
      text + '\n',
      options.horizontal,
      options.vertical,
      options.bold,
      options.invert,
      options.underline,
      options.font,
      options.alignment)
  }
  // console.info(text)
}

export const printSeparator = (options) => {
  const line = '-'.repeat(options.printerConfiguration.line_length)

  if (options.printerConfiguration.library === 'UNIVERSAL') {
    printLine(line, options)
  } else {
    printText(
      `${line}` + '\n',
      options.horizontal,
      options.vertical,
      options.bold,
      options.invert,
      options.underline,
      options.font,
      options.alignment)
  }
  // console.info(`${line}`)
}
